import { Box, Container, Flex } from '@chakra-ui/react';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import ServiceDownBanner from '../../components/ServiceDownBanner';
import { isProd } from '../../config';
import { LDFlags } from '../../constants/flags';
import {
  DASHBOARD,
  MARKETPLACE_CONDITION,
  MARKETPLACE_PHOTOS,
  PAYMENTS_DASHBOARD,
} from '../../constants/urls';
import { useUserDealQuery, useUserDealsQuery } from '../../gql/generated/graphql';
import { useFlag, useMediaCollection } from '../../hooks';
import useCheckFlowStatus from '../../hooks/useCheckFlowStatus';
import { MediaContext } from '../../hooks/useMediaCollection';
import hotjar from '../../services/analytics/hotjar';
import { identify } from '../../services/analytics/rudderstack';
import { DealStateEnum } from '../../utils/types/deal';
import BuyoutDashboard from './Dashboard';
import DashboardDevToolbar from './components/DashboardDevToolbar';
import DashboardFooter from './components/DashboardFooter';
import DashboardHeader from './components/DashboardHeader';
import DashboardLoadingSkeleton from './components/DashboardLoadingSkeleton';
import MarketplaceDashboard from './components/MarketplaceDashboard';
import SavePasswordPrompt from './components/SavePasswordPrompt';
import MarketplaceConditionReportForm from './components/marketplace/conditionReport/MarketplaceConditionReportForm';
import PhotosPage from './components/marketplace/photos/PhotosPage';
import PaymentsPage from './components/payments/PaymentsPage';
import BootedDashboard from './dashboardStates/BootedDashboard';
import NewDealDashboard from './dashboardStates/NewDealDashboard';
import useEffectiveDealFields from './hooks/useEffectiveDealFields';
import useMarketplaceView from './hooks/useMarketplaceView';
import { DealWithFarthestState } from './utils';

export type DealIdentifier = {
  id: string;
  isCobuyer?: boolean;
};

export const NEW_DEAL_VALUE = {
  id: 'new-deal',
} as const;

export const dashboardDealInfoAtom = atom<DealIdentifier | undefined>(undefined);
export const firstVisitAtom = atom<'none' | 'pending' | 'done'>('none');
export const delistModalOpenAtom = atom(false);

// TODO: Rename this file back to Dashboard.tsx once the marketplace dashboard
// work is merged and there is no longer a risk of merge conflicts with the
// buyout dashboard changes in components/Dashboard.tsx.
const Dashboard = () => {
  const dealId = useAtomValue(dashboardDealInfoAtom);
  const mediaCollection = useMediaCollection({ dealId: dealId?.id });
  const serviceDownBannerMessage = useFlag(LDFlags.SERVICE_DOWN_BANNER);
  const { pathname } = useLocation();

  const [hasNoDeals, setHasNoDeals] = useState(false);

  const { loading: dealsLoading } = useUserDealsQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ userDeals }) => setHasNoDeals(!userDeals?.length),
  });
  const { inFlow, car } = useCheckFlowStatus();

  const isNewDeal = dealId?.id === NEW_DEAL_VALUE.id || hasNoDeals;

  const {
    data: userDealQuery,
    loading: dealLoading,
    startPolling,
    stopPolling,
  } = useUserDealQuery({
    fetchPolicy: 'network-only',
    skip: hasNoDeals || dealsLoading,
    variables: {
      id: isNewDeal ? undefined : dealId?.id,
      isCobuyer: dealId?.isCobuyer,
    },
  });

  // In new-deal mode the query runs without an id and the API returns the user's
  // first deal; userDeal is only the deal the dashboard is actually showing.
  const userDeal = isNewDeal ? undefined : userDealQuery?.userDeal ?? undefined;

  useEffect(() => {
    if (userDeal?.id && userDeal?.customer?.id && userDeal?.car?.vin) {
      identify({
        deal_id: userDeal.id,
        customer_id: userDeal.customer.id,
      });
      hotjar.identify(userDeal.car.vin, {
        deal_id: userDeal.id,
        state: userDeal.customer.address?.state || undefined,
      });
    }
  }, [userDeal?.id, userDeal?.customer?.id, userDeal?.car?.vin]);

  const loading = dealsLoading || dealLoading;

  const { state: dealState, requestBoot } = useEffectiveDealFields(userDeal);
  const { isMarketplace, resolving } = useMarketplaceView(userDeal, { startPolling, stopPolling });
  const isDealBooted = dealState === DealStateEnum.Booted || requestBoot;

  return (
    <MediaContext.Provider value={mediaCollection}>
      {serviceDownBannerMessage && (
        <ServiceDownBanner serviceDownBannerMessage={serviceDownBannerMessage} />
      )}
      <Box bgColor={pathname === DASHBOARD && !isMarketplace ? 'white' : 'grayBackground'} h="100%">
        <Flex h="100%" minHeight="100vh" flexDirection="column">
          <DashboardHeader
            inFlow={inFlow}
            car={car}
            marketplaceDealId={isMarketplace ? userDeal?.id ?? undefined : undefined}
          />
          {loading || resolving ? (
            <DashboardLoadingSkeleton />
          ) : (
            <>
              <Flex flex={1} flexDirection="column">
                <SavePasswordPrompt />
                <Switch>
                  {!isMarketplace && (
                    <Route path={[MARKETPLACE_CONDITION, MARKETPLACE_PHOTOS]}>
                      <Redirect to={DASHBOARD} />
                    </Route>
                  )}
                  {isMarketplace && (
                    <Route path={MARKETPLACE_CONDITION}>
                      <MarketplaceConditionReportForm userDeal={userDeal} />
                    </Route>
                  )}
                  {isMarketplace && (
                    <Route path={MARKETPLACE_PHOTOS}>
                      <PhotosPage dealId={userDeal?.id ?? ''} />
                    </Route>
                  )}
                  <Route path={PAYMENTS_DASHBOARD}>
                    {isMarketplace ? (
                      <Redirect to={DASHBOARD} />
                    ) : (
                      <PaymentsPage userDeal={userDeal} />
                    )}
                  </Route>
                  <Route>
                    {isNewDeal ? (
                      <NewDealDashboard inFlow={inFlow} car={car} />
                    ) : isMarketplace ? (
                      <MarketplaceDashboard userDeal={userDeal} isDealBooted={isDealBooted} />
                    ) : isDealBooted ? (
                      <BootedDashboard deal={userDeal as DealWithFarthestState} />
                    ) : (
                      <BuyoutDashboard userDeal={userDeal} loading={loading} />
                    )}
                  </Route>
                </Switch>
              </Flex>
              <Container maxW="container.xl">
                {!isProd && pathname === DASHBOARD && (
                  <DashboardDevToolbar
                    userDeal={userDeal}
                    isNewDeal={isNewDeal}
                    isBooted={isDealBooted}
                    isMarketplace={isMarketplace}
                  />
                )}
                <DashboardFooter />
              </Container>
            </>
          )}
        </Flex>
      </Box>
    </MediaContext.Provider>
  );
};

export default Dashboard;
